import type { RankingStaticContent } from '@core-api/presentation/types/response';
import { useContext, useEffect } from 'react';
import { ScreenshotDataContext } from '@mentimeter/screenshots/ScreenshotDataContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { RankingPresentation } from './RankingPresentation';
import { useRankingResults } from './hooks/use-ranking-results';
import { useRankingPreviewResults } from './hooks/use-ranking-preview-results';

export function RankingPresentationDataHandler() {
  const {
    useQuestion,
    InteractiveCanvasComponents,
    useConfig,
    useCurrentSession,
    usePace,
    isPreviewResultsEnabled,
  } = usePresentationContext();
  const { enableAnimations, mode } = useConfig();
  const { currentStep } = usePace();
  const slide = useQuestion();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as RankingStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    getVisualizationState({ mode, currentStep }) === 'hide-answers';

  const { results, isLoading } = useRankingResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = useRankingPreviewResults({
    isEnabled: isPreviewResultsEnabled,
    choices: interactiveContent.choices,
  });

  const { setSlideIsReady } = useContext(ScreenshotDataContext);

  useEffect(() => {
    if (mode === 'screenshot' && setSlideIsReady) {
      setSlideIsReady(!isLoading);
    }
  }, [mode, isLoading, setSlideIsReady]);

  const choices = interactiveContent.choices;

  return (
    <RankingPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={interactiveContent.description}
      fontSizeOffset={staticContent.textSizeOffset}
      choices={choices.map((choice) => ({
        id: choice.interactiveContentChoiceId,
        label: choice.title,
      }))}
      shouldAnimate={enableAnimations}
      shouldHideResults={shouldHideResults}
      results={isPreviewResultsEnabled ? previewResults : results}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
    />
  );
}
